/**
 * DiscoveryFilterBar — public-facing filter bar for /:
 *   - Horizontal scrolling category tabs (main_categories)
 *   - Advanced filter drawer: country · year · story length · label · tag search
 *   - Free-text search (q=)
 *
 * All state is lifted to the parent (PublicHome) so it can re-fetch
 * /api/stories with the query params. Uses /api/taxonomy for enum
 * options and /api/stories/facets to show live counts next to each
 * filter option (so users don't waste clicks on empty categories).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Filter, Search, X, ChevronDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple in-memory cache so switching filters doesn't re-fetch these.
let _manifestCache = null;
let _facetsCache = null;

export default function DiscoveryFilterBar({ value, onChange }) {
  const [manifest, setManifest] = useState(_manifestCache);
  const [facets, setFacets] = useState(_facetsCache);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value.q || "");

  useEffect(() => {
    if (!_manifestCache) {
      axios.get(`${API}/taxonomy`).then(({ data }) => {
        _manifestCache = data;
        setManifest(data);
      });
    }
    if (!_facetsCache) {
      axios.get(`${API}/stories/facets`).then(({ data }) => {
        _facetsCache = data;
        setFacets(data);
      });
    }
  }, []);

  // Debounced free-text search commit.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((value.q || "") !== q) onChange({ ...value, q: q || undefined });
    }, 350);
    return () => clearTimeout(t);
     
  }, [q]);

  const set = (patch) => onChange({ ...value, ...patch });
  const clear = () => {
    setQ("");
    onChange({});
  };

  const activeCount = useMemo(() => {
    return Object.keys(value || {}).filter(
      (k) => value[k] !== undefined && value[k] !== null && value[k] !== ""
    ).length;
  }, [value]);

  if (!manifest) return null;

  const cats = manifest.main_categories;
  const catCounts = facets?.categories || {};

  return (
    <div className="mb-8" data-testid="discovery-filter-bar">
      {/* Category tabs — All + every main category */}
      <div className="-mx-4 px-4 md:mx-0 md:px-0 overflow-x-auto scrollbar-hide">
        <div className="flex gap-2 min-w-max pb-1">
          <TabButton
            active={!value.main_category}
            onClick={() => set({ main_category: undefined })}
            testid="discovery-cat-all"
          >
            All stories
          </TabButton>
          {cats.map((c) => {
            const n = catCounts[c.slug]?.count ?? null;
            return (
              <TabButton
                key={c.slug}
                active={value.main_category === c.slug}
                onClick={() =>
                  set({
                    main_category:
                      value.main_category === c.slug ? undefined : c.slug,
                  })
                }
                testid={`discovery-cat-${c.slug}`}
              >
                {c.label}
                {n != null && n > 0 && (
                  <span className="ml-1.5 text-[10px] opacity-60">· {n}</span>
                )}
              </TabButton>
            );
          })}
        </div>
      </div>

      {/* Row 2: search + advanced filters + active summary */}
      <div className="mt-4 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[color:var(--muted)]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search titles, tags, places…"
            className="w-full bg-transparent border border-white/10 rounded-full px-9 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
            data-testid="discovery-search-input"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--muted)] hover:text-white"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          onClick={() => setOpen((x) => !x)}
          className={`text-xs px-3 py-2 rounded-full border inline-flex items-center gap-1.5 ${
            open || activeCount > 0
              ? "border-[color:var(--accent)] text-[color:var(--accent)]"
              : "border-white/10 hover:border-white/25"
          }`}
          data-testid="discovery-filter-toggle"
        >
          <Filter className="w-3.5 h-3.5" />
          Filters
          {activeCount > 0 && (
            <span className="text-[10px] bg-[color:var(--accent)] text-black rounded-full px-1.5 py-0.5">
              {activeCount}
            </span>
          )}
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {activeCount > 0 && (
          <button
            onClick={clear}
            className="text-xs text-[color:var(--muted)] hover:text-white underline underline-offset-2"
            data-testid="discovery-filter-clear"
          >
            Reset
          </button>
        )}
      </div>

      {open && (
        <div className="mt-4 panel p-5 space-y-5" data-testid="discovery-filter-panel">
          {/* Labels row */}
          <div>
            <div className="label-eyebrow mb-2">Labels</div>
            <div className="flex flex-wrap gap-2">
              {manifest.story_labels.map((L) => {
                const on = value.label === L.slug;
                const n = facets?.labels?.[L.slug]?.count ?? 0;
                return (
                  <button
                    key={L.slug}
                    onClick={() =>
                      set({ label: on ? undefined : L.slug })
                    }
                    className="text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1"
                    style={{
                      borderColor: on ? L.color : "rgba(255,255,255,0.12)",
                      background: on ? `${L.color}22` : "transparent",
                      color: on ? L.color : "inherit",
                    }}
                    data-testid={`discovery-label-${L.slug}`}
                  >
                    {L.label}
                    {n > 0 && <span className="opacity-70">· {n}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Length + Country + Year */}
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <div className="label-eyebrow mb-2">Length</div>
              <div className="flex flex-wrap gap-1.5">
                {manifest.story_lengths.map((L) => {
                  const on = value.story_length === L.slug;
                  const n = facets?.lengths?.[L.slug]?.count ?? 0;
                  if (n === 0 && !on) return null;
                  return (
                    <button
                      key={L.slug}
                      onClick={() =>
                        set({ story_length: on ? undefined : L.slug })
                      }
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        on
                          ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10 text-[color:var(--accent)]"
                          : "border-white/10 hover:border-white/25"
                      }`}
                      data-testid={`discovery-len-${L.slug}`}
                    >
                      {L.label}
                      {n > 0 && <span className="opacity-60 ml-1">· {n}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-2">Country</div>
              <select
                value={value.country || ""}
                onChange={(e) =>
                  set({ country: e.target.value || undefined })
                }
                className="w-full bg-transparent border border-white/10 rounded-lg px-2 py-1.5 text-sm focus:border-[color:var(--accent)] outline-none"
                data-testid="discovery-country-select"
              >
                <option value="" className="bg-black">
                  Anywhere
                </option>
                {(facets?.countries || []).map((c) => (
                  <option key={c.name} value={c.name} className="bg-black">
                    {c.name} · {c.count}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="label-eyebrow mb-2">Year</div>
              <select
                value={value.year ?? ""}
                onChange={(e) =>
                  set({ year: e.target.value ? Number(e.target.value) : undefined })
                }
                className="w-full bg-transparent border border-white/10 rounded-lg px-2 py-1.5 text-sm focus:border-[color:var(--accent)] outline-none"
                data-testid="discovery-year-select"
              >
                <option value="" className="bg-black">
                  Any year
                </option>
                {(facets?.years || []).map((y) => (
                  <option key={y.year} value={y.year} className="bg-black">
                    {y.year} · {y.count}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tag cloud (top facets) */}
          {facets?.tags?.length > 0 && (
            <div>
              <div className="label-eyebrow mb-2">Popular tags</div>
              <div className="flex flex-wrap gap-1.5">
                {facets.tags.slice(0, 24).map((t) => {
                  const on = value.tag === t.tag;
                  return (
                    <button
                      key={t.tag}
                      onClick={() => set({ tag: on ? undefined : t.tag })}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        on
                          ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10 text-[color:var(--accent)]"
                          : "border-white/10 hover:border-white/25 text-[color:var(--muted)] hover:text-white"
                      }`}
                      data-testid={`discovery-tag-${t.tag}`}
                    >
                      #{t.tag}
                      <span className="opacity-60 ml-1">· {t.count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs whitespace-nowrap px-3.5 py-1.5 rounded-full border transition-colors ${
        active
          ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10 text-[color:var(--accent)]"
          : "border-white/10 hover:border-white/25 text-[color:var(--muted)] hover:text-white"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
